/**
 * ServiceErrorBanner — non-blocking yellow strip rendered at the top
 * of the DashboardLayout when the backend is misbehaving for the
 * currently-logged-in user. Subscribes to the global service-error
 * event bus so any 5xx / network failure caught by `authedFetch`
 * surfaces here automatically.
 *
 * Behaviour:
 *   - Auto-shows when a service error is published.
 *   - Auto-hides after 30s if no new error arrives.
 *   - Manual dismiss with a 5-minute cooldown so the user isn't
 *     bothered repeatedly.
 *   - Bilingual EN/JA (delegates to the same classifier the login
 *     page uses).
 */
import { useEffect, useState } from "react";
import { AlertTriangle, X, RefreshCw } from "lucide-react";
import { subscribeServiceErrors, dismissServiceError } from "@/lib/serviceErrorState";
import { classifyServiceError } from "@/lib/serviceErrors";
import { useLanguage } from "@/lib/i18n";

export default function ServiceErrorBanner() {
  const { ja } = useLanguage();
  const [current, setCurrent] = useState(null);

  useEffect(() => {
    const unsub = subscribeServiceErrors((evt) => setCurrent(evt));
    return unsub;
  }, []);

  // Auto-hide after 30s of no new error.
  useEffect(() => {
    if (!current) return;
    const t = setTimeout(() => setCurrent(null), 30000);
    return () => clearTimeout(t);
  }, [current]);

  if (!current) return null;

  // Re-classify at render time using the CURRENT page language so
  // detail/advice always match the active locale, regardless of what
  // language the upstream classifier baked in at throw time.
  const langInfo = classifyServiceError(current.status, { ja });
  const title = ja ? "一時的な不具合が発生しています" : "Temporary service issue";
  const detail = langInfo.detail || (ja
    ? "自動的に再試行しています。"
    : "Retrying your requests automatically.");
  const advice = langInfo.advice;

  return (
    <div
      role="alert"
      className="bg-amber-50 border-b border-amber-200 px-4 py-2.5 flex items-start gap-3"
      data-testid="service-error-banner"
    >
      <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" strokeWidth={2.2} />
      <div className="flex-1 min-w-0 text-[13px] leading-snug">
        <div className="font-semibold text-amber-900">{title}</div>
        <div className="text-amber-800">
          {detail}{" "}
          {advice && <span className="opacity-80">{advice}</span>}
        </div>
      </div>
      <button
        type="button"
        onClick={() => window.location.reload()}
        className="hidden sm:inline-flex items-center gap-1 text-[12px] font-semibold text-amber-900 hover:text-amber-700 px-2 py-1 rounded hover:bg-amber-100 transition"
        data-testid="service-error-banner-reload"
        title={ja ? "再読み込み" : "Reload"}
      >
        <RefreshCw className="w-3 h-3" strokeWidth={2.4} />
        {ja ? "更新" : "Reload"}
      </button>
      <button
        type="button"
        onClick={() => dismissServiceError()}
        className="text-amber-700 hover:text-amber-900 p-1 rounded hover:bg-amber-100 transition flex-shrink-0"
        data-testid="service-error-banner-dismiss"
        aria-label={ja ? "閉じる" : "Dismiss"}
      >
        <X className="w-4 h-4" strokeWidth={2.4} />
      </button>
    </div>
  );
}
